import { createContext, useContext, useMemo, type ReactNode } from "react";
import Role, { type RoleValue } from "constants/roles";

// Mirrors apps/web's RoleAnywhere naming (interfaces/user.tsx). The role set
// comes from /auth's `roles` field.
export type RoleAnywhere = {
  hasRole: (role: RoleValue) => boolean;
};

const RoleAnywhereContext = createContext<RoleAnywhere | null>(null);

export function useRoleAnywhere(): RoleAnywhere {
  const ctx = useContext(RoleAnywhereContext);
  if (!ctx) {
    throw new Error("useRoleAnywhere must be used inside RoleAnywhereProvider");
  }
  return ctx;
}

export function RoleAnywhereProvider({
  roles,
  children,
}: {
  roles: RoleValue[];
  children: ReactNode;
}) {
  const value = useMemo<RoleAnywhere>(() => {
    const roleSet = new Set(roles);
    // global_admin is an org-wide override on the backend's `role_access` —
    // mirrored here so an admin passes every hasRole check.
    const isGlobalAdmin = roleSet.has(Role.GlobalAdmin);
    return { hasRole: (role) => isGlobalAdmin || roleSet.has(role) };
  }, [roles]);

  return (
    <RoleAnywhereContext.Provider value={value}>
      {children}
    </RoleAnywhereContext.Provider>
  );
}
